import { BASE_SPACING } from './spacing'

/**
 * Max Width Tokens (Internal Design System)
 *
 * Consistent max-width values for containers, modals, and content areas.
 * Use these tokens to maintain consistent layout widths across the application.
 */

export const maxWidth = {
  xxs: '16rem',
  compact: '21.5rem',
  xs: '27.75rem',
  sm: '37.5rem',
  md: '60rem',
  lg: '75rem',
  xl: '96rem',
  full: '100%',
  none: 'none',
  toast: '35.5rem',
  menuSm: '12.5rem',
  menuMd: '16.5rem',
  searchSm: '18.75rem',
  searchMd: '37.5rem',
  sidepanel: '21.5rem',
} as const

export const minWidth = {
  toast: '18rem',
  infoCard: '225px',
  activeStepCard: '510px',
} as const

/**
 * Chart dimension tokens
 */
export const chartDimensions = {
  height: {
    sm: 180,
    md: 220,
    lg: 300,
  },
  pie: {
    innerRadius: {
      none: 0,
      sm: 40,
      md: 70,
    },
    outerRadius: {
      sm: 80,
      md: 95,
      lg: 120,
    },
    paddingAngle: 2,
    cornerRadius: 4,
  },
  gridMinWidth: 420,
} as const

/**
 * KPI dimension tokens
 */
export const kpiDimensions = {
  card: {
    sm: '240px',
    md: '280px',
  },
  graph: {
    width: '403px',
    height: '271px',
  },
  legendSwatch: '20px',
  gridMinWidth: '240px',
} as const

/**
 * Component dimension tokens retained for Sass parity.
 */
export const dropdownDimensions = {
  width: '178px',
  height: '40px',
} as const

export const buttonWidth = {
  fit: 'fit-content',
  fill: '100%',
} as const

export const headerDimensions = {
  editFieldWidth: '18.75rem',
} as const

export const rteModalDimensions = {
  minHeight: '18.75rem',
  maxHeight: '31.25rem',
} as const

export const smsPreviewDimensions = {
  maxWidth: '18.75rem',
  frameRadius: '2.5rem',
  screenRadius: '1.75rem',
  notchWidth: '7rem',
  notchHeight: '1.25rem',
  notchRadius: '0.625rem',
  messageAreaMinHeight: '20rem',
  bubbleMaxWidth: '85%',
  inputHeight: '2rem',
  sendBtnSize: '1.75rem',
  homeHeight: '0.25rem',
  homeWidth: '6rem',
  statusPx: '1.25rem',
} as const

export const emailPreviewDimensions = {
  minHeight: '17.5rem',
  chromeHeight: '1.25rem',
  dotSize: '0.625rem',
  headerOpacity: 0.8,
  dotOpacity: 0.6,
  recipientNameOpacity: 0.7,
  placeholderOffset: '5rem',
  sectionPx: '1.25rem',
} as const

export const topBarDimensions = {
  height: '3.5rem',
} as const

/**
 * Switch dimension tokens
 */
export const switchTokens = {
  // Medium size (default)
  medium: {
    width: '2.125rem',
    height: '1.25rem',
    thumbSize: '1rem',
    translateX: '0.875rem',
    margin: '0.125rem',
  },
  // Small size
  small: {
    width: '1.625rem',
    height: '1rem',
    thumbSize: '0.75rem',
    translateX: '0.625rem',
    margin: '0.125rem',
  },
} as const

/**
 * Dropdown max-height presets
 */
export const dropdownMaxHeight = {
  xs: `${BASE_SPACING * 12.5}rem`,
  sm: `${BASE_SPACING * 25}rem`,
  md: `${BASE_SPACING * 37.5}rem`,
  lg: `${BASE_SPACING * 50}rem`,
  xl: `${BASE_SPACING * 62.5}rem`,
  xxl: `${BASE_SPACING * 75}rem`,
} as const

export type MinWidth = typeof minWidth
export type MaxWidth = typeof maxWidth
export type ChartDimensions = typeof chartDimensions
export type KpiDimensions = typeof kpiDimensions
export type DropdownDimensions = typeof dropdownDimensions
export type ButtonWidth = typeof buttonWidth
export type HeaderDimensions = typeof headerDimensions
export type RteModalDimensions = typeof rteModalDimensions
export type SmsPreviewDimensions = typeof smsPreviewDimensions
export type EmailPreviewDimensions = typeof emailPreviewDimensions
export type TopBarDimensions = typeof topBarDimensions
export type SwitchTokens = typeof switchTokens
export type DropdownMaxHeight = keyof typeof dropdownMaxHeight
